// SPDX-License-Identifier: GPL-3.0-or-later
// Procedural background music. Synthwave and chiptune style, made with WebAudio, no sound files.
//
// The frame loop calls `update(nowMs)` each frame. The music schedules notes about 160 ms ahead
// on the context clock (see music-scheduler.ts). If the frame loop stops, the music stops.
// `setState` changes the arrangement at the next bar. "off" fades out and stops at once.
// Stingers answer feedback events: drain, jackpot, game_over and mode_start.
//
// The music is optional. It carries no information that the player needs: each cue that matters
// has a text cue in the HUD.

import type { FeedbackBus, FeedbackEvent } from "../../src/shared/feedback/index.ts";
import {
  type ActiveMusicState,
  DRAIN_PHRASE,
  GAME_OVER_PHRASE,
  JACKPOT_PHRASE,
  type MusicState,
  type Phrase,
} from "./music-patterns.ts";
import { createSequencer, type Sequencer } from "./music-scheduler.ts";
import { createVoiceKit, type VoiceKit } from "./music-voices.ts";

export type { MusicState } from "./music-patterns.ts";

/**
 * What the music needs from the audio engine. The AudioEngine of engine.ts has these members.
 * This is a structural port: this file does not import the engine.
 */
export interface MusicOutput {
  context(): BaseAudioContext | null;
  musicBus(): GainNode | null;
  onReady(cb: (ctx: BaseAudioContext) => void): () => void;
}

/** The music. */
export interface Music {
  setState(state: MusicState): void;
  update(nowMs: number): void;
  dispose(): void;
  /** The state that plays now. A state that waits for the next bar is not in it yet. */
  state(): MusicState;
}

/** Seconds from the first update to the first note. */
const START_DELAY_S = 0.06;
/** Seconds from an event to its stinger. */
const STINGER_DELAY_S = 0.02;
const FADE_IN_S = 0.4;
const FADE_OUT_S = 0.5;
/** The music removes ended voices at most once in this interval of frame time. */
const PRUNE_INTERVAL_MS = 250;

interface Env {
  readonly ctx: BaseAudioContext;
  readonly bus: GainNode;
  readonly master: GainNode;
  readonly kit: VoiceKit;
}

function buildEnv(ctx: BaseAudioContext, bus: GainNode): Env {
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(bus);
  return { ctx, bus, master, kit: createVoiceKit(ctx, master) };
}

function releaseEnv(env: Env): void {
  env.kit.dispose();
  try {
    env.master.disconnect();
  } catch {
    // A node that is already free needs no action.
  }
}

/** Fade the master gain to `target` over `seconds`, from the value that it has now. */
function rampMaster(env: Env, target: number, seconds: number): void {
  const now = env.ctx.currentTime;
  const gain = env.master.gain;
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(gain.value, now);
  gain.linearRampToValueAtTime(target, now + seconds);
}

/** Lower the master gain fast, hold, then bring it back to full over `recoverS`. */
function dipMaster(env: Env, depth: number, recoverS: number): void {
  const now = env.ctx.currentTime;
  const gain = env.master.gain;
  gain.cancelScheduledValues(now);
  gain.setValueAtTime(gain.value, now);
  gain.linearRampToValueAtTime(depth, now + 0.04);
  gain.linearRampToValueAtTime(1, now + 0.04 + recoverS);
}

function playPhrase(env: Env, phrase: Phrase): void {
  const start = env.ctx.currentTime + STINGER_DELAY_S;
  for (const [i, midi] of phrase.notes.entries()) {
    env.kit.lead(start + i * phrase.stepS, midi, phrase.stepS * 0.9, phrase.vel, phrase.wave, true);
  }
}

/** A drum fill of eight snare hits in one beat, with a hat on each. */
function playFill(env: Env, stepS: number): void {
  const start = env.ctx.currentTime + STINGER_DELAY_S;
  const gap = stepS * 0.5;
  for (let i = 0; i < 8; i++) {
    const vel = 0.5 + i * 0.07;
    env.kit.snare(start + i * gap, vel, true);
    env.kit.hat(start + i * gap, vel * 0.8, false, i, true);
  }
}

/** The mutable state of one music. */
interface MusicCore {
  readonly out: MusicOutput;
  readonly seq: Sequencer;
  env: Env | null;
  wanted: ActiveMusicState | null;
  running: boolean;
  disposed: boolean;
  lastPruneMs: number;
}

function musicEnsureEnv(m: MusicCore): Env | null {
  const ctx = m.out.context();
  const musicBus = m.out.musicBus();
  if (!ctx || !musicBus) return m.env;
  if (m.env && m.env.ctx === ctx && m.env.bus === musicBus) return m.env;
  if (m.env) releaseEnv(m.env);
  m.env = buildEnv(ctx, musicBus);
  // A new master starts at 0. While the music plays, bring it back to full.
  if (m.running) rampMaster(m.env, 1, FADE_IN_S);
  return m.env;
}

function musicBegin(m: MusicCore, e: Env, state: ActiveMusicState): void {
  m.seq.start(state, e.ctx.currentTime + START_DELAY_S);
  rampMaster(e, 1, FADE_IN_S);
  m.running = true;
  m.wanted = null;
}

/** Stingers by event kind. A kind that is not here has no stinger. */
const STINGERS: Readonly<Record<string, (m: MusicCore, env: Env) => void>> = {
  drain(_m, env) {
    dipMaster(env, 0.35, 1);
    playPhrase(env, DRAIN_PHRASE);
  },
  jackpot(_m, env) {
    playPhrase(env, JACKPOT_PHRASE);
  },
  game_over(m, env) {
    dipMaster(env, 0.3, 2.2);
    playPhrase(env, GAME_OVER_PHRASE);
    m.seq.queueState("attract");
  },
  mode_start(m, env) {
    playFill(env, m.seq.stepSeconds());
  },
};

function musicOnEvent(m: MusicCore, ev: FeedbackEvent): void {
  if (!m.running || !m.env) return;
  if (!Object.hasOwn(STINGERS, ev.kind)) return;
  STINGERS[ev.kind]?.(m, m.env);
}

function musicSetState(m: MusicCore, state: MusicState): void {
  if (m.disposed) return;
  if (state === "off") {
    m.wanted = null;
    if (m.running && m.env) rampMaster(m.env, 0, FADE_OUT_S);
    m.running = false;
    return;
  }
  if (m.running) m.seq.queueState(state);
  else m.wanted = state;
}

function musicUpdate(m: MusicCore, nowMs: number): void {
  if (m.disposed || !Number.isFinite(nowMs)) return;
  const e = musicEnsureEnv(m);
  if (!e) return;
  const now = e.ctx.currentTime;
  if (nowMs - m.lastPruneMs >= PRUNE_INTERVAL_MS || nowMs < m.lastPruneMs) {
    m.lastPruneMs = nowMs;
    e.kit.prune(now);
  }
  if (!m.running && m.wanted) musicBegin(m, e, m.wanted);
  if (m.running) m.seq.pump(e.kit, now);
}

function musicDispose(m: MusicCore, offReady: () => void, offBus: () => void): void {
  if (m.disposed) return;
  m.disposed = true;
  m.running = false;
  m.wanted = null;
  offReady();
  offBus();
  if (m.env) releaseEnv(m.env);
  m.env = null;
}

/** Make the music. The bus feeds the stingers. */
export function createMusic(out: MusicOutput, bus: FeedbackBus): Music {
  const m: MusicCore = {
    out,
    seq: createSequencer(),
    env: null,
    wanted: null,
    running: false,
    disposed: false,
    lastPruneMs: Number.NEGATIVE_INFINITY,
  };
  const offReady = out.onReady(() => {
    if (!m.disposed) musicEnsureEnv(m);
  });
  const offBus = bus.subscribe((ev) => musicOnEvent(m, ev));
  return {
    setState: (state) => musicSetState(m, state),
    update: (nowMs) => musicUpdate(m, nowMs),
    state: () => (m.running ? m.seq.state() : (m.wanted ?? "off")),
    dispose: () => musicDispose(m, offReady, offBus),
  };
}
